// src/products/dataroom/utils/fileIconUtils.js
// Utility to get filetype-specific Lucide icons
import { 
  FileText, 
  FileImage, 
  File, 
  FileSpreadsheet,
  Presentation,
  Video,
  Music,
  Archive,
  FileCode,
  FileType
} from 'lucide-react';

/**
 * Get filetype-specific icon component based on filename or file type
 * @param {string} filename - The filename with extension
 * @param {string} fileType - Optional file type from database (MIME type)
 * @returns {React.Component} Lucide icon component
 */
export const getFileTypeIcon = (filename, fileType = null) => {
  if (!filename) return FileText;

  const extension = filename.split('.').pop()?.toLowerCase() || '';
  
  // Check file type from database first (more accurate)
  if (fileType) {
    if (fileType.includes('image/')) return FileImage;
    if (fileType === 'application/pdf') return File;
    if (fileType.includes('word') || fileType.includes('document')) return FileText;
    if (fileType.includes('sheet') || fileType.includes('excel')) return FileSpreadsheet;
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return Presentation;
    if (fileType.includes('video/')) return Video;
    if (fileType.includes('audio/')) return Music;
    if (fileType.includes('zip') || fileType.includes('archive')) return Archive;
    if (fileType.includes('text/')) return FileType;
  }

  // Fallback to extension-based detection
  const iconMap = {
    // Images
    'jpg': FileImage,
    'jpeg': FileImage,
    'png': FileImage,
    'gif': FileImage,
    'bmp': FileImage,
    'svg': FileImage,
    'webp': FileImage,
    
    // PDF
    'pdf': File,
    
    // Documents
    'doc': FileText,
    'docx': FileText,
    'txt': FileType,
    'rtf': FileText,
    'odt': FileText,
    
    // Spreadsheets
    'xls': FileSpreadsheet,
    'xlsx': FileSpreadsheet,
    'csv': FileSpreadsheet,
    'ods': FileSpreadsheet,
    
    // Presentations
    'ppt': Presentation,
    'pptx': Presentation,
    'odp': Presentation,
    
    // Videos
    'mp4': Video,
    'avi': Video,
    'mov': Video,
    'wmv': Video,
    'flv': Video,
    'webm': Video,
    
    // Audio
    'mp3': Music,
    'wav': Music,
    'm4a': Music,
    'flac': Music,
    'aac': Music,
    'ogg': Music,
    
    // Archives
    'zip': Archive,
    'rar': Archive,
    '7z': Archive,
    'tar': Archive,
    'gz': Archive,
    
    // Code
    'js': FileCode,
    'jsx': FileCode,
    'ts': FileCode,
    'tsx': FileCode,
    'html': FileCode,
    'css': FileCode,
    'py': FileCode,
    'java': FileCode,
    'cpp': FileCode,
    'c': FileCode,
    'json': FileCode,
    'xml': FileCode,
  };

  return iconMap[extension] || FileText;
};

/**
 * Get icon color class based on file type
 * @param {string} filename - The filename with extension
 * @param {string} fileType - Optional file type from database
 * @returns {string} Tailwind color class
 */
export const getFileIconColor = (filename, fileType = null) => {
  if (!filename) return 'text-gray-500';

  const extension = filename.split('.').pop()?.toLowerCase() || '';
  
  // Check file type from database first
  if (fileType) {
    if (fileType.includes('image/')) return 'text-green-600';
    if (fileType === 'application/pdf') return 'text-red-600';
    if (fileType.includes('word') || fileType.includes('document')) return 'text-blue-600';
    if (fileType.includes('sheet') || fileType.includes('excel')) return 'text-green-600';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return 'text-orange-600';
    if (fileType.includes('video/')) return 'text-purple-600';
    if (fileType.includes('audio/')) return 'text-pink-600';
    if (fileType.includes('zip') || fileType.includes('archive')) return 'text-gray-600';
    if (fileType.includes('text/')) return 'text-gray-700';
  }

  // Fallback to extension-based colors
  const colorMap = {
    // Images - green
    'jpg': 'text-green-600',
    'jpeg': 'text-green-600',
    'png': 'text-green-600',
    'gif': 'text-green-600',
    'bmp': 'text-green-600',
    'svg': 'text-green-600',
    'webp': 'text-green-600',
    
    // PDF - red
    'pdf': 'text-red-600',
    
    // Documents - blue
    'doc': 'text-blue-600',
    'docx': 'text-blue-600',
    'txt': 'text-gray-700',
    'rtf': 'text-blue-600',
    'odt': 'text-blue-600',
    
    // Spreadsheets - green
    'xls': 'text-green-600',
    'xlsx': 'text-green-600',
    'csv': 'text-green-600',
    'ods': 'text-green-600',
    
    // Presentations - orange
    'ppt': 'text-orange-600',
    'pptx': 'text-orange-600',
    'odp': 'text-orange-600',
    
    // Videos - purple
    'mp4': 'text-purple-600',
    'avi': 'text-purple-600',
    'mov': 'text-purple-600',
    'wmv': 'text-purple-600',
    'flv': 'text-purple-600',
    'webm': 'text-purple-600',
    
    // Audio - pink
    'mp3': 'text-pink-600',
    'wav': 'text-pink-600',
    'm4a': 'text-pink-600',
    'flac': 'text-pink-600',
    'aac': 'text-pink-600',
    'ogg': 'text-pink-600',
    
    // Archives - gray
    'zip': 'text-gray-600',
    'rar': 'text-gray-600',
    '7z': 'text-gray-600',
    'tar': 'text-gray-600',
    'gz': 'text-gray-600',
    
    // Code - yellow
    'js': 'text-yellow-600',
    'jsx': 'text-yellow-600',
    'ts': 'text-yellow-600',
    'tsx': 'text-yellow-600',
    'html': 'text-orange-600',
    'css': 'text-blue-600',
    'py': 'text-blue-600',
    'java': 'text-orange-600',
    'cpp': 'text-blue-600',
    'c': 'text-blue-600',
    'json': 'text-gray-700',
    'xml': 'text-gray-700',
  };

  return colorMap[extension] || 'text-gray-500';
};







